/* eslint-disable @typescript-eslint/no-unsafe-argument */
export const downloadAs = (content: any, filename: string, mimetype: string) => {
  const dataStr = 'data:' + mimetype + ';charset=utf-8,' + encodeURIComponent(content);

  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', dataStr);
  downloadAnchorNode.setAttribute('download', filename);

  document.body.appendChild(downloadAnchorNode);

  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

export const downloadObjectAsJson = (exportObj: any, exportName: string) => {
  downloadAsJson(JSON.stringify(exportObj, null, 4), exportName);
};

export const downloadObjectAsCsv = (exportObj: any, exportName: string) => {
  downloadAs(objToCsvStr(exportObj), exportName + '.csv', 'text/csv');
};

export const downloadAsJson = (fileContent: any, exportName: string) => {
  downloadAs(fileContent, exportName + '.json', 'text/json');
};

export const objToCsvStr = (obj: any[]) => {
  // Handle empty array
  if (!obj || obj.length === 0) {
    return '';
  }

  // Check if the array contains strings (like email addresses)
  if (typeof obj[0] === 'string') {
    // For string arrays, create a simple CSV with a single column
    const header = 'email';
    const rows = obj.map(email => {
      // Escape double quotes within the email
      const escapedEmail = String(email).replace(/"/g, '""');
      // Enclose the email in double quotes
      return `"${escapedEmail}"`;
    });
    return `${header}\n${rows.join('\n')}`;
  }

  // Handle array of objects (original functionality)
  const header = Object.keys(obj[0]).join(',');
  const rows = obj.map(o => {
    const values = Object.values(o).map(value => {
      // Handle nested objects by stringifying them
      const escapedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      // Escape double quotes within the value
      const finalValue = escapedValue.replace(/"/g, '""');
      // Enclose the value in double quotes
      return `"${finalValue}"`;
    });
    return values.join(',');
  });
  return `${header}\n${rows.join('\n')}`;
};


export const downloadAsPEM = (text: any, exportName: string) => {
  downloadAs(text, `${exportName}.pem`, "application/x-pem-file");
};

/**
 * Validates that a URL is safe for downloading (prevents XSS attacks)
 */
const isValidDownloadUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    // Only allow http, https, and data schemes for downloads
    const allowedSchemes = ['http:', 'https:', 'data:'];
    return allowedSchemes.includes(parsedUrl.protocol);
  } catch {
    // Invalid URL format
    return false;
  }
};

/**
 * Sanitizes filename to prevent path traversal and other attacks
 */
const sanitizeFilename = (filename: string): string => {
  // Remove path separators and other potentially dangerous characters
  return filename.replace(/[/\\:*?"<>|]/g, '_').replace(/\.\./g, '_');
};

/**
 * Download the given URL to a file with the given name,
 * opening the file in a new tab so that navigation remains
 * at the current page.
 * 
 * @param url - The URL to download (must be http, https, or data scheme)
 * @param filename - The filename for the download (will be sanitized)
 * @throws Error if the URL is not safe for downloading
 */
export const downloadUrl = (url: string, filename: string) => {
  // Validate URL to prevent XSS attacks
  if (!isValidDownloadUrl(url)) {
    throw new Error('Invalid or unsafe URL for download');
  }

  // Sanitize filename to prevent path traversal attacks
  const safeFilename = sanitizeFilename(filename);

  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', url);
  downloadAnchorNode.setAttribute('download', safeFilename);
  downloadAnchorNode.setAttribute('target', '_blank');

  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};
